import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { useToast } from '../context/ToastContext';
import { FiPackage, FiRefreshCw, FiAlertCircle, FiCheckCircle, FiClock, FiExternalLink, FiX, FiFilter } from 'react-icons/fi';
import '../styles/Admin.css';

const AdminQuickTracking = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [orders, setOrders] = useState([]);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');
    const [expandedOrderId, setExpandedOrderId] = useState(null);
    const [activeTab, setActiveTab] = useState('orders'); // 'orders' or 'tickets'

    useEffect(() => {
        const token = sessionStorage.getItem('adminToken');
        if (!token) {
            navigate('/admin/login');
            return;
        }
        fetchData();
        // Auto-refresh every 30 seconds
        const interval = setInterval(fetchData, 30000);
        return () => clearInterval(interval);
    }, [statusFilter]);

    const getAuthHeaders = () => ({
        headers: { Authorization: `Bearer ${sessionStorage.getItem('adminToken')}` }
    });

    const fetchData = async () => {
        try {
            setRefreshing(true);
            const [ordersRes, ticketsRes] = await Promise.all([
                apiClient.get(`/admin/quick-orders${statusFilter ? `?status=${statusFilter}` : ''}`, getAuthHeaders()),
                apiClient.get('/admin/support-tickets', getAuthHeaders())
            ]);
            setOrders(ordersRes.data.orders || []);
            setTickets(ticketsRes.data.tickets || []);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const getStatusBadge = (status, isLive) => {
        const statusConfig = {
            'pending': { label: 'Pending', color: '#ff9800', icon: FiClock },
            'processing': { label: 'Processing', color: '#2196f3', icon: FiClock },
            'shipped': { label: 'Shipped', color: '#9c27b0', icon: FiPackage },
            'delivered': { label: 'Delivered', color: '#4caf50', icon: FiCheckCircle },
            'delivery_failed': { label: 'Failed', color: '#f44336', icon: FiAlertCircle },
            'cancelled': { label: 'Cancelled', color: '#757575', icon: FiX }
        };
        const config = statusConfig[status] || { label: status, color: '#757575', icon: FiClock };
        const Icon = config.icon;
        return (
            <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: '500',
                background: `${config.color}20`,
                color: config.color
            }}>
                <Icon size={14} />
                {config.label}
                {isLive && <span style={{ marginLeft: '4px', fontSize: '0.7rem' }}>🔴 LIVE</span>}
            </span>
        );
    };

    const getTrackingStatusBadge = (trackingStatus) => {
        return (
            <span style={{
                padding: '2px 8px',
                borderRadius: '8px',
                fontSize: '0.75rem',
                background: '#e3f2fd',
                color: '#1976d2'
            }}>
                {trackingStatus}
            </span>
        );
    };

    const getTicketStatusBadge = (status) => {
        const colors = {
            'open': { bg: '#ffebee', color: '#d32f2f' },
            'in_progress': { bg: '#fff3e0', color: '#f57c00' },
            'resolved': { bg: '#e8f5e9', color: '#388e3c' }
        };
        const config = colors[status] || { bg: '#f5f5f5', color: '#757575' };
        return (
            <span style={{
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '0.8rem',
                fontWeight: '500',
                background: config.bg,
                color: config.color,
                textTransform: 'capitalize'
            }}>
                {status.replace('_', ' ')}
            </span>
        );
    };

    const handleUpdateTicketStatus = async (ticketId, newStatus) => {
        try {
            await apiClient.patch(`/admin/support-tickets/${ticketId}/status`, { status: newStatus }, getAuthHeaders());
            toast.success('Ticket updated');
            fetchData();
        } catch (error) {
            toast.error('Failed to update ticket');
        }
    };

    const openTrackingUrl = (order) => {
        const url = order.tracking?.liveTrackingUrl
            || (order.tracking?.awb ? `https://shiprocket.co/tracking/${order.tracking.awb}`
                : `https://shiprocket.co/tracking?order_id=${order.orderId}`);
        window.open(url, '_blank');
    };

    if (loading) {
        return <div className="admin-container"><p>Loading...</p></div>;
    }

    return (
        <div className="admin-container">
            <div className="admin-header">
                <h1>⚡ Quick Delivery Dashboard</h1>
                <button
                    onClick={fetchData}
                    className="refresh-btn"
                    disabled={refreshing}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                    <FiRefreshCw className={refreshing ? 'spin' : ''} />
                    {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
            </div>

            {/* Stats */}
            <div className="stats-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
                <div className="stat-card" style={{ textAlign: 'center', padding: '16px', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>Total Quick</h3>
                    <p style={{ margin: '8px 0 0', fontSize: '2rem', fontWeight: 'bold', color: '#1976d2' }}>{orders.length}</p>
                </div>
                <div className="stat-card" style={{ textAlign: 'center', padding: '16px', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>Live Now</h3>
                    <p style={{ margin: '8px 0 0', fontSize: '2rem', fontWeight: 'bold', color: '#4caf50' }}>
                        {orders.filter(o => o.tracking?.isLive).length}
                    </p>
                </div>
                <div className="stat-card" style={{ textAlign: 'center', padding: '16px', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>Failed</h3>
                    <p style={{ margin: '8px 0 0', fontSize: '2rem', fontWeight: 'bold', color: '#f44336' }}>
                        {orders.filter(o => o.status === 'delivery_failed').length}
                    </p>
                </div>
                <div className="stat-card" style={{ textAlign: 'center', padding: '16px', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    <h3 style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>Open Tickets</h3>
                    <p style={{ margin: '8px 0 0', fontSize: '2rem', fontWeight: 'bold', color: '#ff9800' }}>
                        {tickets.filter(t => t.status === 'open').length}
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                <button
                    onClick={() => setActiveTab('orders')}
                    style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: activeTab === 'orders' ? 'bold' : 'normal',
                        background: activeTab === 'orders' ? '#1976d2' : '#f5f5f5',
                        color: activeTab === 'orders' ? '#fff' : '#333'
                    }}
                >
                    📦 Quick Orders ({orders.length})
                </button>
                <button
                    onClick={() => setActiveTab('tickets')}
                    style={{
                        padding: '10px 20px',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: activeTab === 'tickets' ? 'bold' : 'normal',
                        background: activeTab === 'tickets' ? '#1976d2' : '#f5f5f5',
                        color: activeTab === 'tickets' ? '#fff' : '#333'
                    }}
                >
                    🎫 Support Tickets ({tickets.length})
                </button>
            </div>

            {/* Orders Tab */}
            {activeTab === 'orders' && (
                <>
                    {/* Filter */}
                    <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <FiFilter />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{ padding: '8px 16px', borderRadius: '8px', border: '1px solid #ddd' }}
                        >
                            <option value="">All Statuses</option>
                            <option value="live">🔴 Live Only</option>
                            <option value="delivered">Delivered</option>
                            <option value="failed">Failed</option>
                            <option value="shipped">Shipped</option>
                        </select>
                    </div>

                    {/* Orders Table */}
                    <div className="admin-table-container" style={{ background: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
                        <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f5f5f5' }}>
                                    <th style={{ padding: '12px', textAlign: 'left' }}>Order ID</th>
                                    <th style={{ padding: '12px', textAlign: 'left' }}>Customer</th>
                                    <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                                    <th style={{ padding: '12px', textAlign: 'left' }}>Tracking</th>
                                    <th style={{ padding: '12px', textAlign: 'left' }}>Amount</th>
                                    <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map(order => (
                                    <React.Fragment key={order.id}>
                                        <tr style={{ borderBottom: '1px solid #eee' }}>
                                            <td style={{ padding: '12px' }}>
                                                <strong>{order.orderId}</strong>
                                                <br />
                                                <span style={{ fontSize: '0.8rem', color: '#666' }}>
                                                    {new Date(order.createdAt).toLocaleDateString('en-IN')}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px' }}>
                                                <div>{order.customer?.name}</div>
                                                <div style={{ fontSize: '0.8rem', color: '#666' }}>{order.customer?.phone}</div>
                                            </td>
                                            <td style={{ padding: '12px' }}>
                                                {getStatusBadge(order.status, order.tracking?.isLive)}
                                            </td>
                                            <td style={{ padding: '12px' }}>
                                                {order.tracking?.lastStatus && getTrackingStatusBadge(order.tracking.lastStatus)}
                                                {order.tracking?.riderName && (
                                                    <div style={{ fontSize: '0.8rem', marginTop: '4px' }}>
                                                        Rider: {order.tracking.riderName}
                                                    </div>
                                                )}
                                            </td>
                                            <td style={{ padding: '12px' }}>₹{order.totalPrice?.toLocaleString('en-IN')}</td>
                                            <td style={{ padding: '12px' }}>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <button
                                                        onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}
                                                        style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', background: '#fff' }}
                                                    >
                                                        {expandedOrderId === order.id ? 'Hide' : 'Timeline'}
                                                    </button>
                                                    {order.tracking?.isLive && (
                                                        <button
                                                            onClick={() => openTrackingUrl(order)}
                                                            style={{ padding: '6px 12px', border: 'none', borderRadius: '4px', cursor: 'pointer', background: '#00b894', color: '#fff', display: 'flex', alignItems: 'center', gap: '4px' }}
                                                        >
                                                            <FiExternalLink size={14} /> Track
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Expanded Timeline */}
                                        {expandedOrderId === order.id && order.tracking?.statusHistory && (
                                            <tr>
                                                <td colSpan="6" style={{ padding: '16px', background: '#f9f9f9' }}>
                                                    <h4 style={{ margin: '0 0 12px' }}>📍 Tracking Timeline</h4>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                        {order.tracking.statusHistory.map((entry, idx) => (
                                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingLeft: '16px', borderLeft: '2px solid #1976d2' }}>
                                                                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#1976d2' }}></span>
                                                                <div>
                                                                    <strong>{entry.status}</strong>
                                                                    {entry.riderName && <span style={{ marginLeft: '8px', color: '#666' }}>({entry.riderName})</span>}
                                                                    <br />
                                                                    <span style={{ fontSize: '0.8rem', color: '#666' }}>
                                                                        {new Date(entry.timestamp).toLocaleString('en-IN')}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                                {orders.length === 0 && (
                                    <tr>
                                        <td colSpan="6" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
                                            No Quick orders found
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {/* Tickets Tab */}
            {activeTab === 'tickets' && (
                <div className="admin-table-container" style={{ background: '#fff', borderRadius: '8px', overflow: 'hidden' }}>
                    <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f5f5f5' }}>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Ticket ID</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Order</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Reason</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Created</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {tickets.map(ticket => (
                                <tr key={ticket._id} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '12px' }}>
                                        <strong>{ticket.ticketId}</strong>
                                        <br />
                                        <span style={{ fontSize: '0.75rem', padding: '2px 6px', background: ticket.type === 'auto' ? '#e3f2fd' : '#f3e5f5', borderRadius: '4px' }}>
                                            {ticket.type}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px' }}>{ticket.orderId}</td>
                                    <td style={{ padding: '12px' }}>{ticket.reason || ticket.subject}</td>
                                    <td style={{ padding: '12px' }}>{getTicketStatusBadge(ticket.status)}</td>
                                    <td style={{ padding: '12px', fontSize: '0.85rem' }}>
                                        {new Date(ticket.createdAt).toLocaleString('en-IN')}
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        {ticket.status !== 'resolved' && (
                                            <select
                                                value={ticket.status}
                                                onChange={(e) => handleUpdateTicketStatus(ticket._id, e.target.value)}
                                                style={{ padding: '6px 10px', borderRadius: '4px', border: '1px solid #ddd' }}
                                            >
                                                <option value="open">Open</option>
                                                <option value="in_progress">In Progress</option>
                                                <option value="resolved">Resolved</option>
                                            </select>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {tickets.length === 0 && (
                                <tr>
                                    <td colSpan="6" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>
                                        No support tickets found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminQuickTracking;
